// See https://github.com/gildas-lormeau/zip.js/issues/212
import {
  BlobWriter,
  TextReader,
  TextWriter,
  Uint8ArrayReader,
  Uint8ArrayWriter,
  ZipReader,
  ZipWriter,
  configure,
} from '@zip.js/zip.js';
import { xml2js } from 'xml-js';
import {
  ATTACHED_FILE_FILE_TYPES,
  ATTACHED_FILE_PRESERVE,
  ATTRS,
  DATA_TRANSFER_ORIGINS,
  LINK_GROUP_TYPES,
  TAGS,
} from './constants';
import { js2xml } from './utils';
import {
  CulturalPracticeEntity,
  FarmEntity,
  PartfieldEntity,
  ProductEntity,
  TaskEntity,
  ValuePresentationEntity,
  WorkerEntity,
} from './entities';
import { CropTypeEntity } from './entities/CropType';
import { DeviceEntity } from './entities/Device';
import { Entity, EntityConstructor, ISO11783Version, ISOFileInformation } from './types';

configure({ useWebWorkers: false });

const MAIN_FILENAME = 'TASKDATA.XML';
const LINKLIST_FILENAME = 'LINKLIST.XML';
const ROOT_FOLDER = 'TASKDATA';

const ISOXML_VERSIONS = {
  4: {
    major: 4,
    minor: 2,
  },
  3: {
    major: 3,
    minor: 3,
  },
};

export type ISOXMLFileOptions = {
  fmisTitle?: string;
  fmisURI?: string;
  version?: ISO11783Version;
  dataTransferOrigin?: string;
  [additionalOptions: string]: any;
};

export class EntitiesManager {
  public static parseISOID(isoXML: string) {
    const match = isoXML.match(/([A-Z]{3})(-?\d+)/);
    if (!match) {
      return null;
    }

    return {
      type: match[1],
      id: parseInt(match[2], 10),
    };
  }

  private entities: { [type: string]: { [xmlId: number]: Entity } } = {};
  private nextIDs: { [type: string]: number } = {};
  private files: { [type: string]: ArrayBuffer[] } = {};
  private rawXML: string = null;

  public options: ISOXMLFileOptions;

  constructor() {}

  private constructISOID(type: string, id: number) {
    return type + id;
  }

  private generateLinklistFile() {
    const linkEntities = [];

    for (const type of Object.keys(this.entities)) {
      for (const xmlId of Object.keys(this.entities[type])) {
        const { id } = this.entities[type][xmlId];
        if (id) {
          linkEntities.push({
            _attributes: {
              [ATTRS.Link.ObjectIdRef]: this.constructISOID(type, +xmlId),
              [ATTRS.Link.LinkValue]: id,
            },
          });
        }
      }
    }
    const json = {
      _declaration: {
        _attributes: {
          version: '1.0',
          encoding: 'utf-8',
        },
      },
      ISO11783LinkList: {
        _attributes: {
          VersionMajor: ISOXML_VERSIONS[this.options.version].major,
          VersionMinor: ISOXML_VERSIONS[this.options.version].minor,
          DataTransferOrigin: DATA_TRANSFER_ORIGINS.FMIS,
          ManagementSoftwareManufacturer: this.options.fmisTitle,
          ManagementSoftwareVersion: '1.0',
        },
        [TAGS.LinkGroup]: {
          _attributes: {
            [ATTRS.LinkGroup.LinkGroupId]: this.constructISOID(TAGS.LinkGroup, 1),
            [ATTRS.LinkGroup.LinkGroupType]: LINK_GROUP_TYPES.UniqueResolvableURIs,
            [ATTRS.LinkGroup.LinkGroupNamespace]: this.options.fmisURI,
          },
          [TAGS.Link]: linkEntities,
        },
      },
    };

    return js2xml(json, { compact: true, spaces: 2 });
  }

  private parseLinklistFile(xml: string) {
    const parsedXML = xml2js(xml, { compact: true, alwaysArray: true });
    if (!parsedXML['ISO11783LinkList'] || !parsedXML['ISO11783LinkList'][0][TAGS.LinkGroup]) {
      return {};
    }

    const matches = {};
    const fmisGroup = parsedXML['ISO11783LinkList'][0][TAGS.LinkGroup].find(
      (groupXml) =>
        groupXml._attributes[ATTRS.LinkGroup.LinkGroupNamespace] === this.options.fmisURI &&
        groupXml._attributes[ATTRS.LinkGroup.LinkGroupType] ===
          LINK_GROUP_TYPES.UniqueResolvableURIs
    );

    if (!fmisGroup) {
      return {};
    }

    (fmisGroup[TAGS.Link] || []).forEach(({ _attributes: attrs }) => {
      matches[attrs[ATTRS.Link.ObjectIdRef]] = { id: attrs[ATTRS.Link.LinkValue] };
    });

    return matches;
  }

  private parseISOXMLTag(xml, matchIDs, files, entityConstructor: EntityConstructor, type: string) {
    const xmlEntities = xml['ISO11783_TaskData'][0][type];
    if (!xmlEntities) {
      return;
    }
    xmlEntities.forEach((xmlEntity) => {
      const { entity, isoId } = entityConstructor.fromXML(xmlEntity, matchIDs, files, this);
      this.registerEntity(entity, isoId);
    });
  }

  private parseISOXML(xml, matchIDs, files, fmisURI) {
    const attrs = xml['ISO11783_TaskData'][0]._attributes;
    const realVersion = parseInt(attrs.VersionMajor, 10);
    this.options = {
      version: realVersion === 4 ? 4 : 3, // we don't support versions < 3 explicitly
      dataTransferOrigin: attrs.DataTransferOrigin,
      fmisTitle: attrs.ManagementSoftwareManufacturer,
      fmisURI,
    };
    this.parseISOXMLTag(xml, matchIDs, files, ValuePresentationEntity, TAGS.ValuePresentation);
    this.parseISOXMLTag(xml, matchIDs, files, WorkerEntity, TAGS.Worker);
    this.parseISOXMLTag(xml, matchIDs, files, CulturalPracticeEntity, TAGS.CulturalPractice);
    this.parseISOXMLTag(xml, matchIDs, files, FarmEntity, TAGS.Farm);
    this.parseISOXMLTag(xml, matchIDs, files, CropTypeEntity, TAGS.CropType);
    this.parseISOXMLTag(xml, matchIDs, files, PartfieldEntity, TAGS.Partfield);
    this.parseISOXMLTag(xml, matchIDs, files, ProductEntity, TAGS.Product);
    this.parseISOXMLTag(xml, matchIDs, files, DeviceEntity, TAGS.Device);
    this.parseISOXMLTag(xml, matchIDs, files, TaskEntity, TAGS.Task);
  }

  // multiple iterations are required because some entities
  // can add more entities of the same type during serialization
  private typeToXML(type: string): any[] {
    if (!this.entities[type]) {
      return [];
    }
    const processedXmlIds = new Set<number>();
    let result = [];
    while (true) {
      const iterResult = Object.keys(this.entities[type])
        .filter((xmlId) => !processedXmlIds.has(+xmlId))
        .map((xmlId) => {
          processedXmlIds.add(+xmlId);
          const entity = this.entities[type][xmlId];
          return entity.toXML(this.constructISOID(type, +xmlId), this);
        });
      if (iterResult.length) {
        result = result.concat(iterResult);
      } else {
        return result;
      }
    }
  }

  public addFile(type: string, file: ArrayBuffer) {
    this.files[type] = this.files[type] || [];
    this.files[type].push(file);

    const idx = this.files[type].length;
    return type + ('0000' + idx).substr(-5);
  }

  // Algorithm:
  //  1. Try to find an entity with the same entity.id. If found, return its isoId
  //  2. If isoId is passed, register the entity with that isoId
  //  3. Assign unique isoId to the entity and return the assigned isoId
  public registerEntity(entity: Entity, isoId?: string): string {
    if (!entity) {
      return null;
    }
    const { type } = entity;
    this.entities[type] = this.entities[type] || {};
    this.nextIDs[type] = this.nextIDs[type] || 1;

    if (entity.id) {
      const xmlId = Object.keys(this.entities[type]).find(
        (id) => this.entities[type][id].id === entity.id
      );

      if (xmlId) {
        return this.constructISOID(type, +xmlId);
      }
    }

    let newXmlId: number;
    if (isoId) {
      const { id } = EntitiesManager.parseISOID(isoId);
      newXmlId = id;

      if (id >= this.nextIDs[type]) {
        this.nextIDs[type] = id + 1;
      }
    } else {
      newXmlId = this.nextIDs[type]++;
    }

    this.entities[type][newXmlId] = entity;

    return this.constructISOID(type, +newXmlId);
  }

  public parseISOXMLFile(data: Blob, dataType: string, fmisURI: string) {
    this.options = {
      fmisURI,
    };
    return new Promise<void>((resolve, reject) => {
      const reader = new FileReader();
      if (dataType === 'application/xml' || dataType === 'text/xml') {
        reader.onload = (e) => {
          const xml = (e.target as FileReader).result as string;
          this.rawXML = xml;
          const parsedXML = xml2js(xml, { compact: true, alwaysArray: true });
          this.parseISOXML(parsedXML, {}, [], fmisURI);
          resolve();
        };
        reader.onerror = reject;

        reader.readAsText(data);
      } else if (dataType === 'application/zip' || dataType === 'application/x-zip-compressed') {
        reader.onload = (e) => {
          const buffer = (e.target as FileReader).result;
          const arr = new Uint8Array(buffer as ArrayBuffer);
          const zipReader = new ZipReader(new Uint8ArrayReader(arr));

          zipReader
            .getEntries()
            .then((entities) => {
              const mainEntity = entities.find((entity) => {
                const filenameParts = entity.filename.split('/');
                return filenameParts[filenameParts.length - 1].toUpperCase() === MAIN_FILENAME;
              });

              const matchEntity = entities.find((entity) => {
                const filenameParts = entity.filename.split('/');
                return filenameParts[filenameParts.length - 1].toUpperCase() === LINKLIST_FILENAME;
              });

              if (!mainEntity) {
                reject(new Error("Zip file doesn't contain TASKDATA.XML"));
              }

              const mainXmlPromise = mainEntity.getData(new TextWriter()).then((xml) => {
                this.rawXML = xml;
                return xml2js(xml, { compact: true, alwaysArray: true });
              });

              const matchXmlPromise = matchEntity
                ? matchEntity.getData(new TextWriter()).then((xml) => {
                    return this.parseLinklistFile(xml);
                  })
                : {};

              const isBin = (entity) => !!entity.filename.match(/\.bin$/i);
              const isXml = (entity) => !!entity.filename.match(/\.xml$/i);

              const filePromises = entities
                .filter(
                  (ent) => ent !== mainEntity && ent !== matchEntity && (isBin(ent) || isXml(ent))
                )
                .map((entity) => {
                  const isBinary = isBin(entity);
                  return entity
                    .getData(isBinary ? new Uint8ArrayWriter() : new TextWriter())
                    .then((fileData) => ({ isBinary, data: fileData, filename: entity.filename }));
                });

              return Promise.all([mainXmlPromise, matchXmlPromise, ...filePromises]);
            })
            .then(([mainXml, matchIDs, ...files]) => {
              const externalReferences =
                mainXml['ISO11783_TaskData'][0][TAGS.ExternalFileReference] || [];

              const extendedMainTags = { ...mainXml['ISO11783_TaskData'][0] };
              externalReferences.forEach((externalRef) => {
                const filename = externalRef._attributes[ATTRS.ExternalFileReference.Filename];
                const file = files.find((file) =>
                  file.filename.match(new RegExp(`${filename}\\.XML$`, 'i'))
                );
                const xml = xml2js(file.data, { compact: true, alwaysArray: true });
                const tags = xml[TAGS.ExternalFileContents][0];
                Object.keys(tags).forEach((tag) => {
                  extendedMainTags[tag] = [
                    ...(extendedMainTags[tag] || []),
                    ...xml[TAGS.ExternalFileContents][0][tag],
                  ];
                });
              });

              const extendedMainXml = {
                ISO11783_TaskData: [extendedMainTags],
              };

              this.parseISOXML(extendedMainXml, matchIDs, files as ISOFileInformation[], fmisURI);
              resolve();
            })
            .catch(reject);
        };
        reader.readAsArrayBuffer(data);
      } else {
        reject(new Error('This data type is not supported'));
      }
    });
  }

  public getEntitiesOfType<T extends Entity>(type: string): T[] {
    return Object.keys(this.entities[type] || []).map((id) => this.entities[type][id] as T);
  }

  public getEntityByIsoId<T extends Entity>(isoId: string): T {
    if (!isoId) {
      return null;
    }
    const { type, id } = EntitiesManager.parseISOID(isoId);
    return this.entities[type] && (this.entities[type][id] as T);
  }

  public getIsoIdByEntity(entity: Entity): string {
    if (!this.entities[entity.type]) {
      return null;
    }
    const isoId = Object.keys(this.entities[entity.type]).find(
      (isoId) => this.entities[entity.type][isoId] === entity
    );
    if (isoId) {
      return this.constructISOID(entity.type, +isoId);
    }
    return null;
  }

  public async generateXML(options: ISOXMLFileOptions = {}): Promise<Blob> {
    this.options = {
      version: 4,
      dataTransferOrigin: DATA_TRANSFER_ORIGINS.FMIS,
      ...options,
    };
    // Order matters
    const mainXMLTags = {
      [TAGS.Task]: this.typeToXML(TAGS.Task),
      [TAGS.Device]: this.typeToXML(TAGS.Device),
      [TAGS.Product]: this.typeToXML(TAGS.Product),
      [TAGS.Partfield]: this.typeToXML(TAGS.Partfield),
      [TAGS.CropType]: this.typeToXML(TAGS.CropType),
      [TAGS.Farm]: this.typeToXML(TAGS.Farm),
      [TAGS.CulturalPractice]: this.typeToXML(TAGS.CulturalPractice),
      [TAGS.Worker]: this.typeToXML(TAGS.Worker),
      [TAGS.ValuePresentation]: this.typeToXML(TAGS.ValuePresentation),
    };

    const attachedFileXML = {
      _attributes: {
        [ATTRS.AttachedFile.FilenameWithExtension]: LINKLIST_FILENAME,
        [ATTRS.AttachedFile.Preserve]: ATTACHED_FILE_PRESERVE.Preserve,
        [ATTRS.AttachedFile.ManufacturerGLN]: '',
        [ATTRS.AttachedFile.FileType]: ATTACHED_FILE_FILE_TYPES.Linklist,
      },
    };

    const json = {
      _declaration: {
        _attributes: {
          version: '1.0',
          encoding: 'utf-8',
        },
      },
      ISO11783_TaskData: {
        _attributes: {
          VersionMajor: ISOXML_VERSIONS[this.options.version].major,
          VersionMinor: ISOXML_VERSIONS[this.options.version].minor,
          DataTransferOrigin: DATA_TRANSFER_ORIGINS.FMIS,
          ManagementSoftwareManufacturer: this.options.fmisTitle,
          ManagementSoftwareVersion: '1.0',
        },
        ...mainXMLTags,
        ...(this.options.version === 4 && { [TAGS.AttachedFile]: attachedFileXML }),
      },
    };

    const mainXML = js2xml(json, { compact: true, spaces: 2 });

    this.rawXML = mainXML;

    const blobWriter = new BlobWriter('application/zip');
    const writer = new ZipWriter(blobWriter);

    await writer.add(`${ROOT_FOLDER}/${MAIN_FILENAME}`, new TextReader(mainXML));
    for (const fileType of Object.keys(this.files)) {
      for (const idx of Object.keys(this.files[fileType])) {
        const file = this.files[fileType][idx];
        const reader = new Uint8ArrayReader(new Uint8Array(file));
        const name = fileType + ('0000' + (+idx + 1)).substr(-5) + '.BIN';
        await writer.add(`${ROOT_FOLDER}/${name}`, reader);
      }
    }

    await writer.add(
      `${ROOT_FOLDER}/${LINKLIST_FILENAME}`,
      new TextReader(this.generateLinklistFile())
    );
    await writer.close();
    return blobWriter.getData();
  }

  getRawXML(formatted: boolean = false) {
    if (!formatted || !this.rawXML) {
      return this.rawXML;
    }

    const parsedXML = xml2js(this.rawXML, { compact: true });
    return js2xml(parsedXML, { compact: true, spaces: 2 });
  }
}
